import { useCallback, useMemo, useRef } from "react";

/**
 * This hook can help to end interactive messages
 * Reminder: If needed, manually call makeNonInteractive when component unmounts
 */
export function useInteractiveMessage(
	commandManager: eXpress.core.ICommandManager,
	options: {
		delayInMs?: number;
	} = {},
) {
	const { delayInMs = 500 } = options;

	/** Remember the last rowId of the last interactive command */
	const lastRowIdChangedRef = useRef<string | undefined>(undefined);

	/** Make the marked interactive message, non-interactive */
	const makeNonInteractive = useCallback(() => {
		const lastChangedRowId = lastRowIdChangedRef.current;
		const currentRowId = commandManager.getRowId();

		// We can close non-interactive so GA event can be logged
		// Check rowID, eg: when undo is pressed or reset command is executed it is changed
		if (lastChangedRowId === currentRowId) {
			commandManager.makeLastNonInteractive();
		}
	}, [commandManager]);

	/** Make the marked interactive message, non-interactive when not called within X milliseconds */
	const makeNonInteractiveDebounced = useMemo(
		() => eXpress.utils.pipe(makeNonInteractive, eXpress.utils.withDebounce({ delayInMs })),
		[makeNonInteractive, delayInMs],
	);

	/** Remember the rowId of the last interactive command that we need to make non-interacive */
	const markInteractiveMessage = useCallback(() => {
		lastRowIdChangedRef.current = commandManager.getRowId(); // Remember the last changed (interactive) rowId
	}, [commandManager]);

	return {
		markInteractiveMessage,

		/** Make the marked interactive message, non-interactive */
		makeNonInteractive,

		makeNonInteractiveDebounced,
	};
}
